import type { SlotClientMessage, SlotServerMessage } from '../../shared/events';

export interface SlotHandlers {
  onMessage: (m: SlotServerMessage) => void;
  onClose: (code: number, reason: string) => void;
}

/** Browser side of the slot socket. Holds a slot for as long as it is open. */
export class SlotClient {
  private ws: WebSocket | null = null;
  private pinger: number | null = null;
  private closedByUs = false;

  constructor(
    private username: string,
    private handlers: SlotHandlers,
  ) {}

  connect(): void {
    this.closedByUs = false;
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const ws = new WebSocket(`${proto}//${location.host}/slot?u=${encodeURIComponent(this.username)}`);
    this.ws = ws;
    ws.onmessage = (ev) => {
      if (typeof ev.data !== 'string' || ev.data === 'pong') return;
      let msg: SlotServerMessage;
      try {
        msg = JSON.parse(ev.data) as SlotServerMessage;
      } catch {
        return;
      }
      if (msg.t === 'hb') {
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ t: 'hb-ack' } satisfies SlotClientMessage));
        return;
      }
      this.handlers.onMessage(msg);
    };
    ws.onopen = () => {
      if (this.closedByUs) {
        // close() was called during the handshake: finish it cleanly so the server frees the slot.
        ws.close(1000, 'client closed');
        return;
      }
      this.pinger = window.setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send('ping');
      }, 25_000);
      window.addEventListener('pagehide', this.onPageHide);
    };
    ws.onclose = (ev) => {
      this.stopPing();
      if (this.ws === ws) this.ws = null;
      if (!this.closedByUs) this.handlers.onClose(ev.code, ev.reason);
    };
    ws.onerror = () => {
      /* onclose follows */
    };
  }

  get open(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /** Asks for a fresh Euler token; `login` asks for one carrying the TikTok login (whitelisted only). */
  renew(login = false): boolean {
    if (!this.open) return false;
    this.ws!.send(JSON.stringify(login ? ({ t: 'renew', login: true } satisfies SlotClientMessage) : ({ t: 'renew' } satisfies SlotClientMessage)));
    return true;
  }

  close(): void {
    this.closedByUs = true;
    this.stopPing();
    const ws = this.ws;
    this.ws = null;
    if (!ws) return;
    // A socket still connecting is closed from onopen; aborting the handshake here would
    // leave the server unsure whether the peer ever went away.
    if (ws.readyState === WebSocket.CONNECTING) return;
    try {
      ws.close(1000, 'client closed');
    } catch {
      /* ignore */
    }
  }

  /** Closing explicitly on unload sends a close frame, so the slot frees right away. */
  private onPageHide = () => this.close();

  private stopPing(): void {
    if (this.pinger) window.clearInterval(this.pinger);
    this.pinger = null;
    window.removeEventListener('pagehide', this.onPageHide);
  }
}
